/**
 * ChatWidget - Reusable AI chat widget component
 *
 * Used for:
 * - Feature 5: Service Discovery Chatbot (floating on public pages)
 * - Feature 6: Application Form Assistant (anchored on form pages)
 *
 * Supports two modes:
 * - "floating": Fixed position chat bubble in bottom-right corner
 * - "inline": Rendered inline within a page layout
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  X,
  Send,
  Loader2,
  Bot,
  User,
  ExternalLink,
  Sparkles,
  ChevronDown,
} from "lucide-react";

// ============================================
// Types
// ============================================

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  /** Optional service recommendations attached to assistant messages */
  recommendations?: ServiceRecommendation[];
  /** Optional grounded references attached to assistant messages */
  grounding?: AssistantGroundingReference[];
  /** Optional next-step suggestions attached to assistant messages */
  suggestedActions?: string[];
}

export interface ServiceRecommendation {
  name: string;
  slug: string;
  category: string;
  description: string;
  confidence: number;
}

export interface AssistantGroundingReference {
  title: string;
  excerpt?: string;
  label?: string;
}

export interface ChatWidgetProps {
  /** Display mode */
  mode: "floating" | "inline";
  /** Title shown in the chat header */
  title: string;
  /** Subtitle/description shown in the chat header */
  subtitle?: string;
  /** Placeholder text for the input field */
  placeholder?: string;
  /** Welcome message shown when chat opens with no messages */
  welcomeMessage?: string;
  /** Suggested starter questions */
  suggestions?: string[];
  /** Handler for sending a message. Returns the assistant response. */
  onSendMessage: (
    message: string,
    history: ChatMessage[],
  ) => Promise<{
    content: string;
    recommendations?: ServiceRecommendation[];
    grounding?: AssistantGroundingReference[];
    suggestedActions?: string[];
  }>;
  /** Optional: externally controlled open state */
  isOpen?: boolean;
  /** Optional: callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Optional: programmatically inject and send a message (change value to trigger) */
  initialMessage?: string;
  /** Optional: unique identifier for the injected message to allow resending the same text */
  initialMessageId?: string;
}

function normalizeAssistantContent(content: string) {
  const normalizedLines = content
    .replace(/\r/g, "")
    .split("\n")
    .map((line) =>
      line
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1")
        .replace(/\*\*(.*?)\*\*/g, "$1")
        .replace(/__(.*?)__/g, "$1")
        .replace(/`([^`]+)`/g, "$1")
        .replace(/^\s{0,3}#{1,6}\s*/, "")
        .replace(/^\s*[-*+•]\s+/, "")
        .replace(/^\s*\d+\.\s+/, "")
        .trim(),
    );

  const paragraphs: string[] = [];
  let currentParagraph: string[] = [];

  for (const line of normalizedLines) {
    if (!line) {
      if (currentParagraph.length > 0) {
        paragraphs.push(currentParagraph.join(" "));
        currentParagraph = [];
      }
      continue;
    }

    currentParagraph.push(line);
  }

  if (currentParagraph.length > 0) {
    paragraphs.push(currentParagraph.join(" "));
  }

  return paragraphs.join("\n\n").trim();
}

// ============================================
// Component
// ============================================

function ChatWidget({
  mode,
  title,
  subtitle,
  placeholder = "Type your message...",
  welcomeMessage,
  suggestions,
  onSendMessage,
  isOpen: controlledIsOpen,
  onOpenChange,
  initialMessage,
  initialMessageId,
}: ChatWidgetProps) {
  const { t } = useTranslation("common");
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = controlledIsOpen ?? internalIsOpen;
  const setIsOpen = useCallback(
    (open: boolean) => {
      if (onOpenChange) {
        onOpenChange(open);
      } else {
        setInternalIsOpen(open);
      }
    },
    [onOpenChange],
  );

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Text for the visually hidden polite region. Only handleSend writes it, so
  // a mount with existing messages announces nothing and the user's own
  // message is never read back.
  const [announcement, setAnnouncement] = useState("");
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // A closed floating panel unmounts the region; don't replay the last reply on reopen
  useEffect(() => {
    if (mode === "floating" && !isOpen) setAnnouncement("");
  }, [mode, isOpen]);

  // Scroll to bottom when messages change
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) {
      return;
    }

    container.scrollTo({
      top: container.scrollHeight,
      behavior: messages.length > 0 ? "smooth" : "auto",
    });
  }, [messages, isLoading]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSend = useCallback(
    async (text?: string) => {
      const messageText = (text || inputValue).trim();
      if (!messageText || isLoading) return;

      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: messageText,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setInputValue("");
      setIsLoading(true);
      setAnnouncement("Thinking…");

      try {
        const response = await onSendMessage(messageText, [
          ...messages,
          userMessage,
        ]);

        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          content: response.content,
          timestamp: new Date(),
          recommendations: response.recommendations,
          grounding: response.grounding,
          suggestedActions: response.suggestedActions,
        };

        setMessages((prev) => [...prev, assistantMessage]);
        setAnnouncement(normalizeAssistantContent(response.content));
      } catch {
        const errorMessage: ChatMessage = {
          id: `error-${Date.now()}`,
          role: "assistant",
          content:
            "I'm sorry, I encountered an error. Please try again in a moment.",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
        setAnnouncement(errorMessage.content);
      } finally {
        setIsLoading(false);
      }
    },
    [inputValue, isLoading, messages, onSendMessage],
  );

  // Auto-send initialMessage when it changes
  const lastInitialMessageRef = useRef<string | undefined>(undefined);
  const lastInitialMessageIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!initialMessage || isLoading) return;

    const alreadyHandled = initialMessageId
      ? initialMessageId === lastInitialMessageIdRef.current
      : initialMessage === lastInitialMessageRef.current;

    if (alreadyHandled) return;

    lastInitialMessageRef.current = initialMessage;
    if (initialMessageId) {
      lastInitialMessageIdRef.current = initialMessageId;
    }

    const timer = window.setTimeout(() => {
      void handleSend(initialMessage);
    }, 150);

    return () => window.clearTimeout(timer);
  }, [handleSend, initialMessage, initialMessageId, isLoading]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  // ============================================
  // Chat Panel Content (shared between modes)
  // ============================================

  const chatContent = (
    <div
      className={`flex flex-col bg-white dark:bg-[rgb(var(--color-surface-container-low))] ${
        mode === "floating"
          ? "rounded-[28px] shadow-ambient-lg w-[400px] h-[560px] max-h-[82vh]"
          : "rounded-2xl shadow-ambient w-full h-[460px]"
      }`}
    >
      {/* Header */}
      <div
        className={`relative flex items-center gap-3 px-5 py-4 bg-gradient-to-br from-[rgb(var(--color-primary-500))] to-[rgb(var(--color-primary-700))] dark:from-surface-container dark:to-surface-container-high text-white shrink-0 overflow-hidden ${
          mode === "floating" ? "rounded-t-[28px]" : "rounded-t-2xl"
        }`}
      >
        {/* Ambient orb */}
        <div
          aria-hidden
          className="pointer-events-none absolute -right-12 -top-16 h-40 w-40 rounded-full bg-secondary-500/20 blur-3xl"
        />
        <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/10 backdrop-blur-sm">
          <Sparkles size={18} className="text-secondary-300" />
        </div>
        <div className="relative flex-1 min-w-0">
          <div className="mb-0.5 flex items-center gap-2">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-secondary-400 opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-secondary-400" />
            </span>
            <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-white/55">
              AI Assistant · Online
            </span>
          </div>
          <h3 className="font-display text-[15px] font-bold leading-tight tracking-tight truncate text-white">
            {title}
          </h3>
          {subtitle && (
            <p className="text-[11px] leading-tight text-white/60 mt-0.5 truncate">
              {subtitle}
            </p>
          )}
        </div>
        {mode === "floating" && (
          <button
            onClick={() => setIsOpen(false)}
            className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-pill text-white/70 hover:text-white hover:bg-white/10 transition-colors focus-visible:outline-none"
            aria-label="Close chat"
          >
            <X size={16} />
          </button>
        )}
      </div>

      <div
        data-testid="chat-live"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement}
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {/* Welcome message */}
        {messages.length === 0 && welcomeMessage && (
          <div className="flex gap-2.5">
            <div className="shrink-0 w-8 h-8 rounded-xl bg-secondary-500/10 flex items-center justify-center">
              <Bot size={15} className="text-secondary-500" />
            </div>
            <div className="bg-surface-subtle dark:bg-[rgb(var(--color-surface-container))] rounded-2xl rounded-tl-md px-4 py-3 max-w-[85%]">
              <p className="text-[13px] leading-relaxed text-on-surface whitespace-pre-line">
                {welcomeMessage}
              </p>
            </div>
          </div>
        )}

        {/* Suggestions (shown when no messages) */}
        {messages.length === 0 && suggestions && suggestions.length > 0 && (
          <div className="space-y-2 mt-3">
            <div className="flex items-center gap-2">
              <span className="h-px w-6 bg-[rgb(var(--color-outline-variant))]/40" />
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-on-surface-variant/85">
                Try asking
              </p>
            </div>
            {suggestions.map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => handleSend(suggestion)}
                className="group flex w-full items-center justify-between gap-2 text-left text-[13px] px-4 py-3 rounded-xl bg-surface-subtle hover:bg-secondary-500/5 text-on-surface transition-colors dark:bg-[rgb(var(--color-surface-container))] dark:hover:bg-secondary-500/10"
              >
                <span className="leading-snug">{suggestion}</span>
                <span className="shrink-0 text-secondary-500 opacity-0 transition-opacity group-hover:opacity-100">
                  →
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Message history */}
        {messages.map((msg) => {
          const isUser = msg.role === "user";
          const content = isUser
            ? msg.content
            : normalizeAssistantContent(msg.content);

          return (
            <div
              key={msg.id}
              className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : ""}`}
            >
              <div
                className={`shrink-0 w-8 h-8 rounded-xl flex items-center justify-center ${
                  isUser
                    ? "bg-[rgb(var(--color-primary-500))] text-white"
                    : "bg-secondary-500/10 text-secondary-500"
                }`}
              >
                {isUser ? <User size={15} /> : <Bot size={15} />}
              </div>

              <div className="max-w-[85%] space-y-2">
                {!isUser &&
                  msg.recommendations &&
                  msg.recommendations.length > 0 && (
                    <div className="space-y-2">
                      {msg.recommendations.map((rec, idx) => (
                        <Link
                          key={idx}
                          to={`/services/${rec.slug}`}
                          aria-label={`${t("search.openService")}: ${rec.name}`}
                          className="group block rounded-2xl bg-surface-subtle p-4 transition-all hover:bg-white hover:shadow-card hover:ring-1 hover:ring-secondary-500/30 dark:bg-[rgb(var(--color-surface-container))] dark:hover:bg-[rgb(var(--color-surface-container-high))]"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-display text-[14px] font-bold text-on-surface group-hover:text-secondary-500 transition-colors">
                                {rec.name}
                              </p>
                              <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant/85">
                                {rec.category}
                              </p>
                              <p className="mt-2 line-clamp-2 text-[12px] leading-relaxed text-on-surface-variant">
                                {rec.description}
                              </p>
                            </div>
                            <ExternalLink
                              size={14}
                              className="mt-0.5 shrink-0 text-on-surface-variant/40 group-hover:text-secondary-500"
                            />
                          </div>
                          <div className="mt-3">
                            <span className="inline-flex items-center gap-1.5 rounded-xl bg-[rgb(var(--color-primary-500))] px-3.5 py-2 text-[11px] font-bold uppercase tracking-wide text-white transition-colors group-hover:bg-[rgb(var(--color-primary-600))]">
                              {t("search.openService")}
                              <ExternalLink size={11} />
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}

                {content && (
                  <div
                    className={`rounded-2xl px-4 py-3 ${
                      isUser
                        ? "bg-[rgb(var(--color-primary-500))] text-white rounded-tr-md"
                        : "bg-surface-subtle dark:bg-[rgb(var(--color-surface-container))] rounded-tl-md"
                    }`}
                  >
                    <p
                      className={`text-[13px] leading-relaxed whitespace-pre-line ${
                        isUser ? "text-white" : "text-on-surface"
                      }`}
                    >
                      {content}
                    </p>
                  </div>
                )}

                {!isUser &&
                  msg.suggestedActions &&
                  msg.suggestedActions.length > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-on-surface-variant/85">
                        Suggested next checks
                      </p>
                      {msg.suggestedActions.map((action, idx) => (
                        <div
                          key={`${msg.id}-action-${idx}`}
                          className="rounded-xl bg-surface-subtle px-3.5 py-2.5 text-[12px] text-on-surface-variant dark:bg-[rgb(var(--color-surface-container))]"
                        >
                          {action}
                        </div>
                      ))}
                    </div>
                  )}

                {!isUser && msg.grounding && msg.grounding.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-on-surface-variant/85">
                      Grounded from
                    </p>
                    {msg.grounding.map((reference, idx) => (
                      <div
                        key={`${msg.id}-grounding-${idx}`}
                        className="rounded-xl bg-surface-subtle px-3.5 py-2.5 dark:bg-[rgb(var(--color-surface-container))]"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-display text-[13px] font-bold text-on-surface">
                            {reference.title}
                          </p>
                          {reference.label && (
                            <span className="inline-flex items-center rounded-pill bg-secondary-500/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-secondary-500">
                              {reference.label}
                            </span>
                          )}
                        </div>
                        {reference.excerpt && (
                          <p className="mt-1.5 text-[11px] leading-relaxed text-on-surface-variant">
                            {reference.excerpt}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex gap-2.5">
            <div className="shrink-0 w-8 h-8 rounded-xl bg-secondary-500/10 flex items-center justify-center">
              <Bot size={15} className="text-secondary-500" />
            </div>
            <div className="bg-surface-subtle dark:bg-[rgb(var(--color-surface-container))] rounded-2xl rounded-tl-md px-4 py-3">
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 bg-secondary-500 rounded-full animate-bounce [animation-delay:0ms]" />
                <div className="w-1.5 h-1.5 bg-secondary-500 rounded-full animate-bounce [animation-delay:150ms]" />
                <div className="w-1.5 h-1.5 bg-secondary-500 rounded-full animate-bounce [animation-delay:300ms]" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="px-5 py-4 shrink-0 bg-white dark:bg-[rgb(var(--color-surface-container-low))]">
        <div className="flex items-center gap-2 rounded-2xl bg-surface-subtle p-1.5 ring-1 ring-on-surface-variant/10 transition-colors focus-within:ring-secondary-500/40 dark:bg-[rgb(var(--color-surface-container-high))] dark:ring-outline-variant/40 dark:focus-within:ring-secondary-500/50">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={isLoading}
            className="flex-1 bg-transparent text-[13px] px-3 py-2.5 text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none disabled:opacity-50"
          />
          <button
            onClick={() => handleSend()}
            disabled={!inputValue.trim() || isLoading}
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-secondary-500 text-white transition-all hover:bg-secondary-600 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Send message"
          >
            {isLoading ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <Send size={15} />
            )}
          </button>
        </div>
        <p className="mt-2 text-center text-[10px] text-on-surface-variant/85">
          AI-powered assistant — responses may not always be accurate
        </p>
      </div>
    </div>
  );

  // ============================================
  // Render
  // ============================================

  if (mode === "inline") {
    return chatContent;
  }

  // Floating mode
  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Chat Panel */}
      {isOpen && (
        <div
          data-guide="ask-ai"
          className="mb-4 animate-in fade-in slide-in-from-bottom-4 duration-200"
        >
          {chatContent}
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`ml-auto flex items-center gap-2.5 rounded-pill px-5 py-3.5 shadow-ambient-lg transition-all duration-200 hover:shadow-ambient-lg hover:-translate-y-0.5 ${
          isOpen
            ? "bg-[rgb(var(--color-surface-container))] text-on-surface hover:bg-[rgb(var(--color-surface-container-high))]"
            : "bg-[rgb(var(--color-primary-500))] text-white hover:bg-[rgb(var(--color-primary-600))]"
        }`}
        aria-label={isOpen ? "Close chat" : "Open chat"}
      >
        {isOpen ? (
          <ChevronDown size={18} />
        ) : (
          <>
            <div className="relative flex h-6 w-6 items-center justify-center">
              <Sparkles size={16} className="text-secondary-300" />
            </div>
            <span className="text-[13px] font-semibold hidden sm:inline">
              Ask AI
            </span>
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-secondary-400 opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-secondary-400" />
            </span>
          </>
        )}
      </button>
    </div>
  );
}

export default ChatWidget;
